import { companyConfig } from "../config/company"

export type RuntimeCompanyProfile = {
  tenantId: string
  shortName: string
  legalName: string
  wmsName: string
  taxId: string
  regon: string
  krs: string
  email: string
  phone: string
  website: string
  address: {
    line1: string
    line2: string
    postalCode: string
    city: string
    countryCode: string
  }
  locale: string
  timezone: string
  assets: {
    loginLogo: string
    labelLogo: string
  }
  alt: {
    loginLogo: string
    labelLogo: string
  }
  revisionNumber: number
}

const storageKey = "company_profile_cache_v1"

const emptyCompanyProfile: RuntimeCompanyProfile = {
  tenantId: "00000000-0000-0000-0000-000000000001",
  shortName: "",
  legalName: "",
  wmsName: "",
  taxId: "",
  regon: "",
  krs: "",
  email: "",
  phone: "",
  website: "",
  address: {
    line1: "",
    line2: "",
    postalCode: "",
    city: "",
    countryCode: "PL",
  },
  locale: "pl-PL",
  timezone: "Europe/Warsaw",
  assets: {
    loginLogo: "",
    labelLogo: "/assets/images/stockerra_logo.png",
  },
  alt: {
    loginLogo: "Logo firmy",
    labelLogo: "Logo firmy",
  },
  revisionNumber: 0,
}

const legacyDevFallbackProfile: RuntimeCompanyProfile = {
  tenantId: "00000000-0000-0000-0000-000000000001",
  shortName: companyConfig.shortName,
  legalName: companyConfig.legalName,
  wmsName: companyConfig.wmsName,
  taxId: companyConfig.taxId,
  regon: companyConfig.regon,
  krs: companyConfig.krs,
  email: companyConfig.email,
  phone: companyConfig.phoneFax,
  website: companyConfig.website,
  address: {
    line1: companyConfig.addressLines[0] ?? "",
    line2: companyConfig.addressLines[1] ?? "",
    postalCode: "65-936",
    city: "Zielona Góra",
    countryCode: "PL",
  },
  locale: "pl-PL",
  timezone: "Europe/Warsaw",
  assets: {
    loginLogo: companyConfig.assets.loginLogo,
    labelLogo: companyConfig.assets.labelLogo,
  },
  alt: {
    loginLogo: companyConfig.alt.loginLogo,
    labelLogo: companyConfig.alt.labelLogo,
  },
  revisionNumber: 0,
}

export const fallbackCompanyProfile: RuntimeCompanyProfile = import.meta.env.DEV
  ? legacyDevFallbackProfile
  : emptyCompanyProfile

export function writeStoredCompanyProfile(profile: RuntimeCompanyProfile) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(profile))
  } catch {
    // ignore storage errors
  }
}

export function readStoredCompanyProfile(): RuntimeCompanyProfile {
  if (typeof window === "undefined") return fallbackCompanyProfile
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return fallbackCompanyProfile
    const parsed = JSON.parse(raw) as Partial<RuntimeCompanyProfile>
    return {
      ...fallbackCompanyProfile,
      ...parsed,
      address: {
        ...fallbackCompanyProfile.address,
        ...(parsed.address ?? {}),
      },
      assets: {
        ...fallbackCompanyProfile.assets,
        ...(parsed.assets ?? {}),
        labelLogo:
          parsed.assets?.labelLogo?.trim() ||
          fallbackCompanyProfile.assets.labelLogo,
      },
      alt: {
        ...fallbackCompanyProfile.alt,
        ...(parsed.alt ?? {}),
      },
    }
  } catch {
    return fallbackCompanyProfile
  }
}
